import { MfDate, Period } from '@campaign-manager/core/model';

export interface RelativeDateRange {
  start: MfDate;
  end: MfDate;
}

/**
 * Calculates a relative date range based on a period and count.
 *
 * @param period - The period type (DAY, WEEK, MONTH, QUARTER, YEAR)
 * @param count - Number of periods to go back (negative) or forward (positive).
 *                Use 0 for "to date" calculations (e.g., month to date).
 * @param relativeToPeriodStart - If true, the end date is calculated relative to the period's end.
 *                                 If false, the end date is yesterday.
 * @param today - Optional reference date for testing. Defaults to MfDate.today().
 * @returns Object with start and end MfDate values
 *
 * @example
 * // Last 7 days (today is 2024-01-15)
 * calculateRelativeDateRange(Period.DAY, -7)
 * // Returns: { start: 2024-01-08, end: 2024-01-14 }
 *
 * @example
 * // Last month (today is 2024-02-15)
 * calculateRelativeDateRange(Period.MONTH, -1, true)
 * // Returns: { start: 2024-01-01, end: 2024-01-31 }
 *
 * @example
 * // Month to date (today is 2024-01-15)
 * calculateRelativeDateRange(Period.MONTH, 0)
 * // Returns: { start: 2024-01-01, end: 2024-01-14 }
 */
export function calculateRelativeDateRange(
  period: Period,
  count: number,
  relativeToPeriodStart = false,
  today: MfDate = MfDate.today(),
): RelativeDateRange {
  const start = today.truncate(period).addPeriod(period, count);
  const end = relativeToPeriodStart
    ? today.endOf(period).addPeriod(period, count)
    : today.addPeriod(Period.DAY, -1);

  return { start, end };
}
